import React from 'react';
import { formatApiError } from '../../utils/errorMessage';

// Starting balance and its date on the Add/Edit Account form.
// Saved through the reconciliation opening-balance endpoint, which also
// recalculates the account balance (starting balance + rows after the date).
const day = (v) => (v ? String(v).slice(0, 10) : '');

// Pulls the two fields out of the form data. Returns null when both are
// blank or unchanged, an error string when only one is filled in.
export function startingBalanceChange(data, account) {
  const amount = String(data.opening_balance ?? '').trim();
  const date = String(data.opening_balance_date ?? '').trim();
  const provisional = data.opening_balance_provisional === 'on' || data.opening_balance_provisional === true;
  const note = String(data.opening_balance_note ?? '').trim();
  const timing = data.opening_balance_timing === 'start_of_day' ? 'start_of_day' : 'end_of_day';
  delete data.opening_balance_timing;
  delete data.opening_balance;
  delete data.opening_balance_date;
  delete data.opening_balance_provisional;
  delete data.opening_balance_note;
  if (!amount && !date) return null;
  if (!amount || !date) return { error: 'Enter both a starting balance and its date.' };
  if (!Number.isFinite(Number(amount))) return { error: 'Starting balance must be a number.' };
  const a = account || {};
  const same = timing === 'end_of_day' && a.opening_balance !== null && a.opening_balance !== undefined
    && Number(a.opening_balance) === Number(amount) && day(a.opening_balance_date) === date
    && !!a.opening_balance_provisional === provisional && (a.opening_balance_note || '') === note;
  if (same) return null;
  return { opening_balance: amount, opening_balance_date: date, opening_balance_timing: timing, opening_balance_provisional: provisional, opening_balance_note: note };
}

export function startingBalanceMessage(before, after, format) {
  if (before === after) {
    return `Current balance stays at ${format(after)} (no transactions fall between the old and new dates). Save the new starting balance?`;
  }
  return `Current balance will change from ${format(before)} to ${format(after)}. Save the new starting balance?`;
}

// Asks the server what the new starting balance would do (nothing is saved)
// and confirms with the person when the current balance would change.
export async function confirmStartingBalance(api, accountId, opening, format, ask = (m) => window.confirm(m)) {
  try {
    const preview = (await api.put(`/accounts/${accountId}/opening-balance?preview=1`, opening)).data || {};
    const before = Number(preview.previous_balance) || 0;
    const after = Number(preview.current_balance) || 0;
    // Always shown, so the person sees the effect even when nothing moves.
    return ask(startingBalanceMessage(before, after, format));
  } catch (err) {
    return ask(`The effect on the current balance could not be checked (${formatApiError(err, 'please try again')}). Save the new starting balance anyway?`);
  }
}

// Saved after the account itself (account-edit permission). A failure here
// is reported clearly but does not undo the account save.
export async function saveStartingBalance(api, accountId, opening, notify = (m) => window.alert(m)) {
  try {
    await api.put(`/accounts/${accountId}/opening-balance`, opening);
    return true;
  } catch (err) {
    const why = formatApiError(err, 'please try again');
    notify(`The account was saved, but the starting balance was not: ${why}`);
    return false;
  }
}

export default function StartingBalanceFields({ account, error }) {
  const a = account || {};
  const box = 'w-full border rounded px-3 py-2';
  return (
    <fieldset className="border rounded p-3 space-y-2">
      <legend className="text-sm font-medium px-1">Starting balance</legend>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">Balance
          <input type="number" step="0.01" name="opening_balance" defaultValue={a.opening_balance ?? ''} className={box} />
        </label>
        <label className="text-sm">As of date
          <input type="date" name="opening_balance_date" defaultValue={day(a.opening_balance_date)} className={box} />
        </label>
      </div>
      <label className="text-sm block">This balance is from
        <select name="opening_balance_timing" defaultValue="end_of_day" className={box}>
          <option value="end_of_day">The end of that day (after that day's transactions)</option>
          <option value="start_of_day">The start of that day (before that day's transactions)</option>
        </select>
      </label>
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" name="opening_balance_provisional" defaultChecked={!!a.opening_balance_provisional} />
        Provisional / temporary (stays in the starting-balance review until cleared or older history is imported)
      </label>
      <label className="text-sm block">Reference / note
        <input type="text" name="opening_balance_note" maxLength={500} defaultValue={a.opening_balance_note || ''} className={box} placeholder="e.g. estimate until the June statement is imported" />
      </label>
      <p className="text-xs text-gray-500">The balance on your statement for that date. Transactions after it (typed in or imported) move the balance from there. A start-of-day balance is saved as the end of the day before, so that day's transactions count.</p>
      {a.is_liability && <p className="text-xs text-gray-500">For cards and loans, enter the amount owed as a negative number (for example -500.00).</p>}
      <p className="text-xs text-gray-500">Transactions dated before the starting balance date are history: they stay in your records and reports but do not change the current balance.</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </fieldset>
  );
}
